<template>
  <input
    type="text"
    :id="inputId"
    :aria-label="ariaLabel || (inputId ? null : placeholder)"
    :placeholder="placeholder"
    @input="onInputChange"
    @keydown="onInputKeydown"
    v-model="addressAutoComplete"
    class="radarAutoCompleteInput"
    :class="{ addressInput: from == 'home' }"
    ref="autocompleteInput"
    role="combobox"
    aria-autocomplete="list"
    :aria-expanded="addressAutoCompleteListIsOpen ? 'true' : 'false'"
    :aria-controls="addressSuggestionListId"
    :aria-activedescendant="activeOptionId"
  />
  <div
    v-if="addressAutoCompleteListIsOpen"
    :id="addressSuggestionListId"
    ref="addressSuggestions"
    class="addressSuggestions"
    :class="{ darkMode: branding.dark_mode_enabled }"
    :style="{ top: label ? '70px' : '50px' }"
    role="listbox"
  >
    <div v-if="addressAutoCompleteList?.length > 0">
      <div
        v-for="(address, index) in addressAutoCompleteList"
        :key="index"
        @click="addressSelected(address)"
        @mouseenter="activeSuggestionIndex = index"
        :id="`address-option-${_uid}-${index}`"
        class="suggestion"
        :class="{ suggestionActive: activeSuggestionIndex === index }"
        role="option"
        :aria-selected="activeSuggestionIndex === index ? 'true' : 'false'"
      >
        <img src="@/assets/icons/marker.svg" alt="" />
        <p>{{ address.formattedAddress }}</p>
      </div>
    </div>
    <div v-else>
      <p class="suggestion">{{ t('addressSearch.noResult', { query: addressAutoComplete }) }}</p>
    </div>
  </div>
  <p class="srOnly" aria-live="polite">{{ suggestionAnnouncement }}</p>
</template>

<script>
import { mapState } from 'vuex'

import externalApi from '@/api/externalApi'
import i18n from '@/plugins/i18n'

export default {
  data() {
    return {
      addressAutoComplete: '',
      debounce: null,
      addressAutoCompleteList: null,
      addressAutoCompleteListIsOpen: false,
      activeSuggestionIndex: -1,
      addressSuggestionListId: '',
    }
  },
  props: ['placeholder', 'from', 'reqiresAddress', 'label', 'countryCodes', 'inputId', 'ariaLabel'],
  computed: {
    ...mapState(['branding', 'storeConfig']),
    activeOptionId() {
      if (this.activeSuggestionIndex < 0) {
        return null
      }
      return `address-option-${this._uid}-${this.activeSuggestionIndex}`
    },
    suggestionAnnouncement() {
      if (!this.addressAutoCompleteListIsOpen) {
        return ''
      }
      const suggestionsCount = this.addressAutoCompleteList?.length || 0
      if (suggestionsCount === 0) {
        return this.t('addressSearch.noResultsAnnouncement', { query: this.addressAutoComplete })
      }
      return this.t('addressSearch.suggestionsAvailable', { count: suggestionsCount })
    },
    computedCountryCodes() {
      // If countryCodes prop is provided, use it
      if (this.countryCodes && this.countryCodes.length > 0) {
        return this.countryCodes.join(',')
      }

      // Default fallback: store_locator_country_codes -> country_code -> US
      const storeLocatorCodes = this.storeConfig?.store_locator_country_codes

      if (Array.isArray(storeLocatorCodes) && storeLocatorCodes.length > 0) {
        return storeLocatorCodes.join(',')
      }

      if (typeof storeLocatorCodes === 'string' && storeLocatorCodes.trim()) {
        return storeLocatorCodes
          .split(/[,\s]+/)
          .map((c) => c.trim())
          .filter(Boolean)
          .join(',')
      }

      return this.storeConfig?.country_code || 'US'
    },
  },
  emits: ['addressSelected'],
  methods: {
    t(key, params = {}) {
      return i18n.global.t(key, params)
    },
    onInputChange() {
      this.activeSuggestionIndex = -1
      this.getAddressAutoSuggestions()
    },
    onInputKeydown(event) {
      const suggestionsCount = this.addressAutoCompleteList?.length || 0
      if (!this.addressAutoCompleteListIsOpen || suggestionsCount === 0) {
        if (event.key === 'Escape') {
          this.addressAutoCompleteListIsOpen = false
        }
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % suggestionsCount
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.activeSuggestionIndex = this.activeSuggestionIndex <= 0 ? suggestionsCount - 1 : this.activeSuggestionIndex - 1
        return
      }

      if (event.key === 'Enter' && this.activeSuggestionIndex >= 0) {
        event.preventDefault()
        this.addressSelected(this.addressAutoCompleteList[this.activeSuggestionIndex])
        return
      }

      if (event.key === 'Escape') {
        this.addressAutoCompleteListIsOpen = false
      }
    },
    getAddressAutoSuggestions() {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (!this.addressAutoComplete?.trim()) {
          this.addressAutoCompleteList = null
          this.addressAutoCompleteListIsOpen = false
          return
        }
        let layer = this.reqiresAddress ? 'address' : 'address,coarse'
        externalApi
          .getAddresses(this.addressAutoComplete, layer, this.computedCountryCodes)
          .then((response) => {
            this.addressAutoCompleteList = response.data.addresses
            this.addressAutoCompleteListIsOpen = true
            this.activeSuggestionIndex = -1
          })
          .catch((error) => {
            console.log(error)
          })
      }, 300)
    },
    addressSelected(addressData) {
      this.$emit('addressSelected', addressData)
      this.addressAutoCompleteListIsOpen = false
      this.addressAutoComplete = addressData.formattedAddress
      this.activeSuggestionIndex = -1
    },
    resetAddressInput() {
      this.addressAutoComplete = ''
      this.addressAutoCompleteList = null
      this.addressAutoCompleteListIsOpen = false
      this.activeSuggestionIndex = -1
      this.$refs.autocompleteInput.value = ''
    },
  },
  created() {
    this.addressSuggestionListId = `address-suggestions-${this._uid}`
  },
  mounted() {
    // if upsellDropdown is open, close it when user clicks outside of it
    document.addEventListener('click', (e) => {
      if (this.addressAutoCompleteListIsOpen) {
        if (!this.$refs.addressSuggestions?.contains(e.target)) {
          this.addressAutoCompleteListIsOpen = false
        }
      }
    })
  },
}
</script>

<style scoped>
.addressSuggestions {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  position: absolute;
  background-color: #ffffff;
  width: 100%;
  padding: 5px 0px;
  z-index: 9999;
}
.suggestion {
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
}
.suggestion:last-child {
  border-bottom: none;
}
.suggestion img {
  height: 12px;
}
.suggestion p {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
}
.suggestion:hover {
  background-color: var(--gray-color);
}
.suggestionActive {
  background-color: var(--gray-color);
}
.addressInput {
  border: none;
  border-bottom: 1px solid var(--border-color);
  border-radius: 0;
  padding: 10px 0px;
}
.remove {
  position: absolute;
  top: 3px;
  right: 0px;
  padding: 10px;
  cursor: pointer;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  border-radius: 5px;
}
.remove a {
  display: flex;
  align-items: center;
  justify-content: center;
}
.remove a img {
  height: 14px;
  width: 14px;
}
.darkMode {
  background-color: var(--primary-color);
}
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>